import {
  getUploadLimits,
  UploadLimits,
  uploadMultipleFiles,
  UserDocumentType,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { KeyLabelPair } from "@/config/types";
import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";

export type NoteState =
  | {
      wantsNote: true;
      note: string;
    }
  | { wantsNote: false };

type UseUploadParams = {
  onCloseDialog: () => void;
} | void;

export function useUpload(params: UseUploadParams) {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang, selectedMandateId, selectedMandate } = useContext(PspContext);
  const [uploadLimits, setUploadLimits] = useState<UploadLimits>();
  useEffect(() => {
    getUploadLimits().then(({ data }) => {
      setUploadLimits(data);
    });
  }, []);
  const uploadButtonDisabledTimeoutRef = useRef<NodeJS.Timeout>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const [documentType, setDocumentType] = useState<
    UserDocumentType | undefined
  >(
    selectedMandate?.allowedUploadTypes.length === 1
      ? selectedMandate?.allowedUploadTypes[0]
      : undefined,
  );
  const [noteState, setNoteState] = useState<NoteState>({ wantsNote: false });
  const [isUploading, setIsUploading] = useState(false);
  const [payrollMonth, setPayrollMonth] = useState<number>();
  const fileTooBig = useMemo(() => {
    if (!selectedFiles || !uploadLimits) {
      return false;
    }
    let totalSize = 0;
    const fileTooBig = selectedFiles.reduce<false | string>((prev, cur) => {
      // eslint-disable-next-line react-hooks/immutability
      totalSize += cur.size;
      if (cur.size > uploadLimits.maxFileSizeBytes) {
        return cur.name;
      }
      return prev;
    }, false);
    if (fileTooBig) {
      return fileTooBig;
    }
    return totalSize > uploadLimits.maxRequestSizeBytes;
  }, [uploadLimits, selectedFiles]);
  const documentTypeOptions: KeyLabelPair<UserDocumentType>[] | undefined =
    selectedMandate
      ? [
          ...(selectedMandate.allowedUploadTypes.includes("accounting")
            ? [
                {
                  key: "accounting" as UserDocumentType,
                  label: translate("accounting", lang),
                },
              ]
            : []),
          ...(selectedMandate.allowedUploadTypes.includes("tax_return")
            ? [
                {
                  key: "tax_return" as UserDocumentType,
                  label: translate("tax_return", lang),
                },
              ]
            : []),
          ...(selectedMandate.allowedUploadTypes.includes("payroll_accounting")
            ? [
                {
                  key: "payroll_accounting" as UserDocumentType,
                  label: translate("payroll_accounting", lang),
                },
              ]
            : []),
        ]
      : undefined;

  const willBeSentToName = selectedMandate
    ? documentType === "accounting" || documentType === "tax_return"
      ? selectedMandate.buchhalter?.name
      : documentType === "payroll_accounting"
        ? selectedMandate.lohnBuchhalter?.name
        : undefined
    : undefined;

  // this is needed to make sure that while the dialog is closing
  // (during the animation), the button is not briefly enabled such that the
  // user can upload the same document multiple times even if he spam-clicks.
  // the button is reenabled only 500 ms after closing the dialog
  const [uploadButtonDisabled, setUploadButtonDisabled] = useState(false);
  useEffect(() => {
    if (isUploading) {
      setUploadButtonDisabled(true);
    } else {
      uploadButtonDisabledTimeoutRef.current = setTimeout(function () {
        setUploadButtonDisabled(false);
      }, 500);
    }
    return function () {
      if (uploadButtonDisabledTimeoutRef.current) {
        clearTimeout(uploadButtonDisabledTimeoutRef.current);
      }
    };
  }, [isUploading]);

  const isDataInvalid =
    documentType === undefined ||
    (documentType === "payroll_accounting" && payrollMonth === undefined) ||
    (noteState.wantsNote === true && noteState.note === "");

  function onFilesChange(filesOrEvent: File[] | ChangeEvent<HTMLInputElement>) {
    if (filesOrEvent) {
      setSelectedFiles((prev) =>
        Array.isArray(filesOrEvent)
          ? [...(prev ?? []), ...filesOrEvent]
          : [...(prev ?? []), ...(filesOrEvent.target.files ?? [])],
      );
    }
  }

  function onClose() {
    params?.onCloseDialog();
    setTimeout(() => {
      setSelectedFiles(null);
      setPayrollMonth(undefined);
      setDocumentType(undefined);
      setNoteState({ wantsNote: false });
    }, 250);
  }

  function resolvePayrollDate(monthValue: number, today: Date) {
    const isBefore16 = today.getDate() < 16;

    // normal months (0–11)
    if (monthValue <= 11) {
      const base = new Date(today);
      base.setMonth(today.getMonth() + (isBefore16 ? -1 : 0));

      const target = new Date(base);
      target.setMonth(base.getMonth() + (monthValue - base.getMonth()));

      return {
        month: monthValue,
        year: target.getFullYear(),
      };
    }

    // special months 13–15
    const extraYear =
      today.getMonth() === 0 && today.getDate() < 16
        ? today.getFullYear() - 1
        : today.getFullYear();

    return {
      month: monthValue,
      year: extraYear,
    };
  }

  const today = new Date();
  function onUpload() {
    const payrollData =
      documentType === "payroll_accounting" && payrollMonth
        ? resolvePayrollDate(payrollMonth, today)
        : undefined;
    if (selectedMandateId && selectedFiles && documentType) {
      setIsUploading(true);
      uploadMultipleFiles({
        path: { mandate_id: selectedMandateId },
        query: {
          document_type: documentType,
          note: noteState.wantsNote ? noteState.note : null,
          month: payrollData?.month,
          year: payrollData?.year,
        },
        body: { files: selectedFiles },
      }).then(({ error, status }) => {
        if (error) {
          if (status === 409) {
            toast(translate("unsupportedFiletype", lang));
          } else if (status === 413) {
            toast(translate("fileTooLarge", lang));
          } else {
            toast(translate("somethingWentWrong", lang));
          }
        } else {
          toast(
            translate("documentsUploadedSuccessfully", lang, {
              "{documentNames}": selectedFiles
                .map((file) => file.name)
                .join(", "),
            }),
          );
          setIsUploading(false);
          setTimeout(() => {
            setSelectedFiles(null);
          }, 250);
          onClose();
        }
      });
    }
  }

  function onChangeWantsNote(wantsNote: boolean) {
    if (wantsNote) {
      setNoteState({ wantsNote: true, note: "" });
      return;
    }

    setNoteState({ wantsNote: false });
  }

  function onChangeNote(value: string) {
    setNoteState((prev) => ({ ...prev, note: value }));
  }

  function addMonths(date: Date, offset: number) {
    const d = new Date(date);
    d.setMonth(d.getMonth() + offset);
    return d;
  }

  const isBefore16 = today.getDate() < 16;
  const startOffset = isBefore16 ? -1 : 0;
  const months = Array.from({ length: 7 }, (_, i) => {
    const d = addMonths(today, startOffset + i);
    return {
      month: d.getMonth(), // 0–11
      year: d.getFullYear(),
    };
  });

  const extraYear =
    today.getMonth() === 0 && today.getDate() < 16
      ? today.getFullYear() - 1
      : today.getFullYear();

  return {
    lang,
    selectedMandate,
    onUpload,
    onFilesChange,
    selectedFiles,
    documentType,
    setDocumentType,
    onChangeWantsNote,
    noteState,
    onChangeNote,
    uploadButtonDisabled,
    setSelectedFiles,
    payrollMonth,
    setPayrollMonth,
    onClose,
    extraYear,
    months,
    isDataInvalid,
    documentTypeOptions,
    willBeSentToName,
    fileTooBig,
    uploadLimits,
  };
}
